// Orchestrates the three per-table sync passes. Ordering rules:
//   - PER TABLE: pull-then-push, ALWAYS serially. On the first sync after
//     sign-in (cursor === null) the pull runs content-based dedupe (see
//     dedupe.ts) that rewrites local clientIds to match server rows;
//     running push concurrently would race and re-push the row under its
//     old clientId. On subsequent syncs, the same serial ordering also
//     prevents a parallel-pull-deletes-locally + parallel-push-re-uploads
//     race when a tombstone from another device arrives at the same time
//     as our own outgoing push.
//   - ACROSS tables: also SERIAL. The pull pass uses
//     `withDeleteHookSuppressed` (in db.ts) which sets a module-level
//     boolean; running two tables in parallel can toggle that flag at
//     wrong moments and cause one table's hook-suppressed delete to fire
//     the tombstone-creating sub-transaction (which fails because the
//     enclosing parent tx only includes the one table). The performance
//     hit is negligible (three tables × two round-trips) and the
//     correctness gain is real.

import { isSupabaseConfigured } from '../supabase/client';
import { syncCheckInsDown, syncCheckInsUp } from './checkInsSync';
import { syncSponsorTasksDown, syncSponsorTasksUp } from './sponsorTasksSync';
import { syncTenthStepsDown, syncTenthStepsUp } from './tenthStepsSync';
import type { SyncAllResult, SyncResult, SyncTableName } from './types';
import { EMPTY_SYNC_RESULT } from './types';

function emptyAll(): SyncAllResult {
  return {
    pushed: 0,
    pulled: 0,
    conflicts: 0,
    errors: [],
    byTable: {
      check_ins: { ...EMPTY_SYNC_RESULT },
      tenth_steps: { ...EMPTY_SYNC_RESULT },
      sponsor_tasks: { ...EMPTY_SYNC_RESULT },
    },
  };
}

function mergeResults(a: SyncResult, b: SyncResult): SyncResult {
  return {
    pushed: a.pushed + b.pushed,
    pulled: a.pulled + b.pulled,
    conflicts: a.conflicts + b.conflicts,
    errors: [...a.errors, ...b.errors],
  };
}

async function syncOneTable(
  table: SyncTableName,
  userId: string,
): Promise<SyncResult> {
  switch (table) {
    case 'check_ins': {
      const down = await syncCheckInsDown(userId);
      const up = await syncCheckInsUp(userId);
      return mergeResults(down, up);
    }
    case 'tenth_steps': {
      const down = await syncTenthStepsDown(userId);
      const up = await syncTenthStepsUp(userId);
      return mergeResults(down, up);
    }
    case 'sponsor_tasks': {
      const down = await syncSponsorTasksDown(userId);
      const up = await syncSponsorTasksUp(userId);
      return mergeResults(down, up);
    }
  }
}

export async function syncAll(userId: string): Promise<SyncAllResult> {
  if (!isSupabaseConfigured() || !userId) return emptyAll();

  const tables: SyncTableName[] = ['check_ins', 'tenth_steps', 'sponsor_tasks'];
  const out = emptyAll();
  for (const table of tables) {
    const r = await syncOneTable(table, userId);
    out.byTable[table] = r;
    out.pushed += r.pushed;
    out.pulled += r.pulled;
    out.conflicts += r.conflicts;
    out.errors.push(...r.errors);
  }
  return out;
}
